import type { GameEvent } from '../schema';

/** General life texture: identity, habits, small decisions with long tails. */
export const LIFE_EVENTS: GameEvent[] = [
  {
    id: 'life.movingOut',
    title: 'Your own front door',
    description: 'Independence costs more than it looks like on paper, and is worth more than it costs.',
    category: 'life',
    pools: ['pool.young', 'pool.finance.early'],
    weight: 8,
    unique: true,
    ageMax: 30,
    choices: [
      {
        id: 'alone',
        label: 'Get your own place',
        hint: 'costly',
        tags: ['independence'],
        outcomes: [
          {
            weight: 1,
            label: 'Lived alone',
            narrative: 'Expensive, quiet, and the first place that was entirely yours.',
            tone: 'positive',
            effects: [
              { k: 'fixedExpenses', mode: 'mult', value: 1.45 },
              { k: 'stat', stat: 'happiness', delta: 9 },
              { k: 'skill', skill: 'selfManagement', delta: 6 },
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: -5 },
            ],
          },
        ],
      },
      {
        id: 'share',
        label: 'Share with others',
        hint: 'safe',
        tags: ['social'],
        outcomes: [
          {
            weight: 1,
            label: 'Moved in with others',
            narrative: 'Cheaper, louder, and two of them are still in your life decades later.',
            tone: 'positive',
            effects: [
              { k: 'fixedExpenses', mode: 'mult', value: 1.14 },
              { k: 'person', op: 'add', kind: 'friend', tags: ['housemate'] },
              { k: 'skill', skill: 'social', delta: 5 },
              { k: 'stat', stat: 'happiness', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'stay',
        label: 'Stay at home and save',
        hint: 'safe',
        tags: ['security', 'discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed put',
            narrative: 'Three years of very low costs and a deposit that appeared out of nowhere.',
            tone: 'neutral',
            effects: [
              { k: 'habit', habit: 'saving', delta: 14 },
              { k: 'cash', amount: 9000 },
              { k: 'stat', stat: 'happiness', delta: -5 },
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: 6 },
              { k: 'relationshipAll', kind: 'parent', dim: 'conflict', delta: 8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'life.firstBigPurchase',
    title: 'The thing you have always wanted',
    description: 'A car, a watch, a trip. Affordable if you ignore the opportunity cost.',
    category: 'finance',
    pools: ['pool.young', 'pool.finance.early', 'pool.leisure'],
    weight: 7,
    cooldown: 6,
    requires: [{ k: 'num', path: 'cash', op: '>=', value: 9000 }],
    choices: [
      {
        id: 'buy',
        label: 'Buy it',
        hint: 'mixed',
        tags: ['lifestyle'],
        outcomes: [
          {
            weight: 1,
            label: 'Bought it',
            narrative: 'Real, immediate joy with a half-life of about fourteen months.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -2.2, note: 'big purchase' },
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'habit', habit: 'spending', delta: 8 },
              { k: 'schedule', afterYears: 2, label: 'The novelty wore off', effects: [{ k: 'stat', stat: 'happiness', delta: -7 }] },
            ],
          },
        ],
      },
      {
        id: 'wait',
        label: 'Put it in the market instead',
        hint: 'slow',
        tags: ['discipline', 'finance'],
        outcomes: [
          {
            weight: 1,
            label: 'Invested it',
            narrative: 'Nobody notices this decision, including you, until twenty years later.',
            tone: 'neutral',
            effects: [
              { k: 'invest', op: 'buy', assetId: 'growth', amount: 8000 },
              { k: 'habit', habit: 'investing', delta: 10 },
              { k: 'stat', stat: 'happiness', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'life.badHabit',
    title: 'It has become a habit',
    description: 'Something you do most nights to make the day stop. It works, briefly.',
    category: 'wellbeing',
    pools: ['pool.young', 'pool.stress'],
    weight: 6,
    cooldown: 6,
    requires: [{ k: 'num', path: 'stress', op: '>=', value: 55 }],
    choices: [
      {
        id: 'stop',
        label: 'Deal with it now',
        hint: 'costly',
        tags: ['discipline', 'health'],
        outcomes: [
          {
            weight: 66,
            label: 'Broke it',
            narrative: 'Three unpleasant months and then it simply was not a feature of your life.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'trait', trait: 'discipline', op: '>=', value: 58 }], multiplier: 1.8 },
              { conditions: [{ k: 'num', path: 'supportNetwork', op: '>=', value: 55 }], multiplier: 1.6 },
            ],
            effects: [
              { k: 'stat', stat: 'health', delta: 10 },
              { k: 'stat', stat: 'energy', delta: 9 },
              { k: 'trait', trait: 'discipline', delta: 6 },
              { k: 'achievement', id: 'ach.habitBroken', label: 'Broke a habit that was winning', category: 'health' },
            ],
          },
          {
            weight: 34,
            label: 'It came back',
            narrative: 'Four months clear, then a bad week.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'health', delta: -6 },
              { k: 'trait', trait: 'optimism', delta: -3 },
              { k: 'intention', op: 'add', id: 'int.habit', label: 'Deal with the habit', topic: 'health', urgency: 40, decayEventId: 'life.habitCost' },
            ],
          },
        ],
      },
      {
        id: 'manage',
        label: 'Keep it within limits',
        hint: 'risky',
        outcomes: [
          {
            weight: 1,
            label: 'Managed it',
            narrative: 'Under control, on the whole, most weeks.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'health', delta: -4 },
              { k: 'stat', stat: 'stress', delta: -5 },
              { k: 'intention', op: 'add', id: 'int.habit', label: 'Deal with the habit', topic: 'health', urgency: 28, decayEventId: 'life.habitCost' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'life.habitCost',
    title: 'Twenty years of that',
    description: 'The habit you managed rather than addressed has submitted its invoice.',
    category: 'health',
    pools: ['pool.inertia'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'ack',
        label: 'Face it',
        outcomes: [
          {
            weight: 1,
            label: 'The bill arrived',
            narrative: 'Nothing sudden. An accumulation.',
            tone: 'negative',
            effects: [
              { k: 'health', op: 'condition', id: 'cond.habitRelated', label: 'Habit-related condition', severity: 48 },
              { k: 'stat', stat: 'health', delta: -16 },
              { k: 'regret', op: 'add', id: 'regret.habit', label: 'Never dealt with the habit', weight: 20 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'life.creativeUrge',
    title: 'You want to make something',
    description: 'A record, a novel, a piece of furniture. No commercial justification whatsoever.',
    category: 'life',
    pools: ['pool.leisure', 'pool.young'],
    weight: 6,
    cooldown: 5,
    weightMods: [{ conditions: [{ k: 'skill', skill: 'creative', op: '>=', value: 45 }], multiplier: 1.6 }],
    choices: [
      {
        id: 'finish',
        label: 'Commit to finishing it',
        hint: 'slow',
        tags: ['creative', 'discipline'],
        inertiaRisk: 0.4,
        outcomes: [
          {
            weight: 1,
            label: 'Finished it',
            narrative: 'It is imperfect and it exists, which is the entire distinction.',
            tone: 'positive',
            effects: [
              { k: 'counter', name: 'creativeWorks', delta: 1 },
              { k: 'skill', skill: 'creative', delta: 8 },
              { k: 'stat', stat: 'purpose', delta: 12 },
              { k: 'aspiration', op: 'progress', defId: 'dream.craft', delta: 34 },
              { k: 'stat', stat: 'energy', delta: -6 },
            ],
          },
        ],
      },
      {
        id: 'dabble',
        label: 'Start it and see',
        hint: 'mixed',
        outcomes: [
          {
            weight: 38,
            label: 'It turned into something',
            narrative: 'You did not intend to finish it and then you did.',
            tone: 'positive',
            effects: [
              { k: 'counter', name: 'creativeWorks', delta: 1 },
              { k: 'skill', skill: 'creative', delta: 5 },
              { k: 'stat', stat: 'purpose', delta: 7 },
            ],
          },
          {
            weight: 62,
            label: 'Abandoned',
            narrative: 'It joined the others in the drawer.',
            tone: 'neutral',
            effects: [
              { k: 'skill', skill: 'creative', delta: 2 },
              { k: 'inertia', op: 'pressure', delta: 5 },
              { k: 'intention', op: 'add', id: 'int.creative', label: 'Finish the thing you started', topic: 'leisure', urgency: 22 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'life.valuesShift',
    title: 'You want different things now',
    description: 'What motivated you at twenty-five has quietly stopped working.',
    category: 'life',
    pools: ['pool.regret', 'pool.milestone.45', 'pool.stress'],
    weight: 5,
    cooldown: 10,
    requires: [{ k: 'age', min: 33 }],
    choices: [
      {
        id: 'realign',
        label: 'Change what you are optimising for',
        hint: 'mixed',
        tags: ['reflection', 'change'],
        outcomes: [
          {
            weight: 1,
            label: 'Reoriented',
            narrative: 'You stopped chasing the thing you had stopped wanting. Obvious, and rarely done.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 14 },
              { k: 'trait', trait: 'ambition', delta: -6 },
              { k: 'trait', trait: 'empathy', delta: 5 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 10 },
              { k: 'aspiration', op: 'adopt', defId: 'dream.contribute' },
            ],
          },
        ],
      },
      {
        id: 'persist',
        label: 'Finish what you started',
        hint: 'mixed',
        tags: ['discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Kept going',
            narrative: 'Commitment or momentum. From the inside they are hard to distinguish.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'performance', delta: 6 },
              { k: 'stat', stat: 'purpose', delta: -5 },
              { k: 'trait', trait: 'conscientiousness', delta: 4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'life.generosity',
    title: 'Someone needs help you can give',
    description: 'Not money. Time, or a room, or a reference that costs you something if it goes wrong.',
    category: 'social',
    pools: ['pool.social', 'pool.legacy'],
    weight: 6,
    cooldown: 4,
    choices: [
      {
        id: 'help',
        label: 'Help',
        hint: 'mixed',
        tags: ['contribution', 'generosity'],
        outcomes: [
          {
            weight: 78,
            label: 'It mattered',
            narrative: 'A disproportionate amount of good for a bounded amount of inconvenience.',
            tone: 'positive',
            effects: [
              { k: 'counter', name: 'contributions', delta: 1 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'relationshipAll', dim: 'trust', delta: 6 },
              { k: 'stat', stat: 'energy', delta: -4 },
            ],
          },
          {
            weight: 22,
            label: 'It cost you',
            narrative: 'They let you down and it was visible to people whose opinion matters.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: -7 },
              { k: 'stat', stat: 'stress', delta: 8 },
              { k: 'trait', trait: 'empathy', delta: -3 },
            ],
          },
        ],
      },
      {
        id: 'no',
        label: 'Stay out of it',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Declined',
            narrative: 'Sensible. You thought about it for a while afterwards.',
            tone: 'neutral',
            effects: [{ k: 'stat', stat: 'happiness', delta: -2 }],
          },
        ],
      },
    ],
  },
  {
    id: 'life.quietYear',
    title: 'Nothing much happened',
    description: 'A year of ordinary weeks. These are the ones that do the accumulating.',
    category: 'life',
    pools: ['pool.young', 'pool.leisure', 'pool.social', 'pool.retired.leisure'],
    weight: 5,
    cooldown: 2,
    automatic: true,
    choices: [
      {
        id: 'pass',
        label: 'Continue',
        outcomes: [
          {
            weight: 1,
            label: 'An ordinary year',
            narrative: 'Work, meals, weekends. Nothing to record and quite a lot to be said for it.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'stress', delta: -3 },
              { k: 'stat', stat: 'energy', delta: 3 },
            ],
          },
        ],
      },
    ],
  },
];
